import { useEffect, useRef, useState } from 'react';
import { Button, Callout } from '@neshane/blueberry';
import { Modal } from '../../../common/components/Modal';
import { PageLoader } from '../../../common/components/PageLoader';
import styles from './TermsModal.module.css';

const SCROLL_THRESHOLD = 24;

/**
 * Scroll-to-accept terms & conditions modal.
 *
 * The approve button stays disabled until the user scrolls the terms body to
 * the bottom. Once approved, `onApprove` is called; the caller owns recording
 * the acceptance (via the terms API) and flipping the KYC consent checkbox.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose - close/cancel handler
 * @param {{id: number, version: number, title: string, content: string}|null} props.terms - active terms version
 * @param {boolean} props.loading - terms still being fetched
 * @param {string|null} props.error - fetch/accept error message
 * @param {() => Promise<boolean>|void} props.onApprove - called when user approves
 * @param {boolean} props.approving - accept request in flight
 * @param {string} [props.approveText] - approve button label
 */
function TermsModal({
    isOpen,
    onClose,
    terms,
    loading = false,
    error = null,
    onApprove,
    approving = false,
    approveText = 'تایید و ادامه',
}) {
    const bodyRef = useRef(null);
    const [canApprove, setCanApprove] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setCanApprove(false);
        }
    }, [isOpen]);

    // Enable approval immediately when the terms content fits without
    // scrolling (no scroll event would ever fire to reach the bottom).
    useEffect(() => {
        if (!isOpen || loading) return;
        const el = bodyRef.current;
        if (!el) return;
        const fits = el.scrollHeight - el.clientHeight <= SCROLL_THRESHOLD;
        if (fits) setCanApprove(true);
    }, [isOpen, loading, terms, error]);

    const handleScroll = () => {
        const el = bodyRef.current;
        if (!el) return;
        const reachedBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_THRESHOLD;
        if (reachedBottom) setCanApprove(true);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={terms?.title || 'قوانین و مقررات'}
            subtitle={terms ? `نسخه ${terms.version}` : undefined}
            iconName="document-duplicate"
            iconVariant="info"
            size="large"
            footer={
                <div className={styles.footer}>
                    <Button
                        variant="secondaryNeutral"
                        text="انصراف"
                        isDisabled={approving}
                        onClick={onClose}
                    />
                    <Button
                        variant="primaryBrand"
                        text={approveText}
                        isDisabled={!canApprove || approving}
                        isLoading={approving}
                        onClick={() => onApprove && onApprove()}
                    />
                </div>
            }
        >
            <div className={styles.modal}>
                {loading ? (
                    <div className={styles.loader}><PageLoader /></div>
                ) : (
                    <>
                        {error && <Callout variant="alert" title={error} className={styles.error} />}
                        <div ref={bodyRef} className={styles.scrollBody} onScroll={handleScroll}>
                            {terms ? (
                                <pre className={styles.content}>{terms.content}</pre>
                            ) : (
                                <p className={styles.empty}>محتویات قوانین در دسترس نیست.</p>
                            )}
                        </div>
                        <div className={styles.helper}>
                            {canApprove
                                ? 'برای تایید، دکمه «تایید و ادامه» را بزنید.'
                                : 'برای فعال‌شدن دکمه تایید، تا انتهای متن را مطالعه کنید.'}
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
}

export default TermsModal;